/**
 * KB-4564 ruling B-hist (card a8de05c5, directive e28bc540; condition 3 amended by 903ee0a2) — the programs the
 * location's offers were last built from, read from version history, so "Publish to offers" can let a window that
 * merely copied that old program follow a re-time (`releaseOldProgramWindow`). Shared by the server
 * `publishOffersForLocation` and the web `updateAllOffersForLocation`; each supplies its own SDK's read.
 *
 * THE PREMISE. Every dashboard save writes the LIVE campLocations doc — on the version path (which also updates the
 * latest draft) as well as the KB-3308 live-only one (campLocationUpdateService). So the program editor's re-time is
 * on the live doc before any location publish, and "re-time, then Publish to offers" is the common sequence.
 *
 * WHICH VERSION. Every location publish marks a `campLocationVersions` doc `status: "published"` and stamps its id on
 * the live doc as `publishedVersionId` (versionPublishService). The read is the existing published-version query
 * (`getPublishedVersion`: locationRef ==, status == "published", orderBy timestamp desc — the kb-ff index that already
 * serves it) with limit 2, newest first. The newest row is the CURRENT published version and must be the live doc's
 * `publishedVersionId`. The old program is "the version the offers were last built from":
 *   - the CURRENT published version, when the live programs (key + timings) have moved on from it (a saved re-time);
 *   - otherwise (live = current: the location was published since the re-time) the version BEFORE it, row 2.
 * A draft is never read (status filter), so the latest draft, newer than the published version, is ignored.
 *
 * WHY A WRONG GUESS IS SAFE. The old program is only a comparison source: a group is released only when its four
 * stored keys equal what that program produced, the stamp exemption still applies, and the carry still keeps every
 * other held group. Offers last built from anything else equal neither snapshot and are carried (ruling A): a STALE
 * offer, never republished since an earlier publish, keeps its window. The worst case is a window left as it was.
 *
 * FAILS SAFE TO "UNKNOWN" (undefined → every held window carried) — never throws, never blocks:
 *   - the read throws or returns no rows; the current row has no `variations` array;
 *   - the live doc has no `publishedVersionId` (never published on the version path), or the newest published row
 *     is not it (a rollback re-published an older version, or the read is stale);
 *   - live = current and there is no row before it, or it has no `variations` array.
 * The KB-3308 flag: the web seam skips the read when it is on. The server cannot see the flag (review round 5 F2); a
 * live-only location writes no versions, so there it is covered only by the equality guard above — a stale
 * `publishedVersionId` left from before the flag yields a current snapshot that the offers either copy (and then
 * follow the live program, as ruling B intends) or do not (carried).
 */

export type PublishedVersionRow = { id: string; variations: unknown };

const asArray = (v: unknown): ReadonlyArray<Record<string, unknown>> | null =>
  Array.isArray(v) ? (v as ReadonlyArray<Record<string, unknown>>) : null;

/** Key-sorted JSON, so two Firestore maps with the same content compare equal whatever their key order. */
function stable(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stable).join(",")}]`;
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    return `{${Object.keys(o).filter((k) => o[k] !== undefined).sort().map((k) => `${JSON.stringify(k)}:${stable(o[k])}`).join(",")}}`;
  }
  return JSON.stringify(v ?? null);
}

/** The programs' window-relevant identity: each variation's key and timings, by key. */
function programsSignature(variations: ReadonlyArray<Record<string, unknown>>): string {
  const rows = variations
    .filter((v) => v && typeof v === "object")
    .map((v) => ({ key: v.key ?? null, timings: v.timings ?? null }))
    .sort((a, b) => String(a.key).localeCompare(String(b.key)));
  return stable(rows);
}

/**
 * The prior published programs, or undefined when they cannot be known safely (see the file comment).
 * `newestPublished` is the published-version read, newest first (at most two rows are looked at).
 */
export function pickPriorPublishedVariations(params: {
  publishedVersionId: unknown;
  liveVariations: unknown;
  newestPublished: ReadonlyArray<PublishedVersionRow> | null | undefined;
}): ReadonlyArray<Record<string, unknown>> | undefined {
  const { publishedVersionId, newestPublished } = params;
  const live = asArray(params.liveVariations);
  if (typeof publishedVersionId !== "string" || !publishedVersionId || !live) return undefined;
  if (!Array.isArray(newestPublished) || newestPublished.length === 0) return undefined;
  const [current, prior] = newestPublished;
  if (!current || current.id !== publishedVersionId) return undefined;
  const currentVariations = asArray(current.variations);
  if (!currentVariations) return undefined;
  // The live programs have moved on from the current published version (a saved, unpublished re-time): the offers
  // were last built from the current version.
  if (programsSignature(currentVariations) !== programsSignature(live)) return currentVariations;
  // They have not (the location was published since the re-time): the version before it.
  if (!prior || prior.id === current.id) return undefined;
  return asArray(prior.variations) ?? undefined;
}

/**
 * Runs the seam's read and picks the prior programs; any failure is "unknown" (undefined), logged, never thrown.
 * `liveLocation` is the live campLocations doc the seam already holds.
 */
export async function readPriorPublishedVariations(
  liveLocation: Record<string, unknown> | null | undefined,
  readNewestPublished: () => Promise<ReadonlyArray<PublishedVersionRow>>,
  warn: (msg: string, err?: unknown) => void = () => undefined,
): Promise<ReadonlyArray<Record<string, unknown>> | undefined> {
  if (!liveLocation || typeof liveLocation.publishedVersionId !== "string" || !liveLocation.publishedVersionId) return undefined;
  try {
    const newestPublished = await readNewestPublished();
    return pickPriorPublishedVariations({
      publishedVersionId: liveLocation.publishedVersionId,
      liveVariations: liveLocation.variations,
      newestPublished,
    });
  } catch (err) {
    warn("[KB-4564] prior published version unreadable; every held window is carried", err);
    return undefined;
  }
}

/** The `timings` of the prior variation with `variationKey`, `{}` when it had none, or undefined when unknown. */
export function priorProgramTimingsFor(
  priorVariations: ReadonlyArray<{ key?: unknown; timings?: unknown }> | null | undefined,
  variationKey: unknown,
): unknown {
  if (!Array.isArray(priorVariations) || typeof variationKey !== "string") return undefined;
  const prior = priorVariations.find((v) => v && v.key === variationKey);
  if (!prior) return undefined;
  return prior.timings && typeof prior.timings === "object" ? prior.timings : {};
}
